import React, { createContext, useCallback, useContext, useMemo, ReactNode } from 'react';
import { IntlProvider } from 'react-intl';
import { SupportedLocale, messages, defaultLocale } from '../i18n';
import { setStoredLocale } from '../i18n/locale-path';

interface IntlContextType {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
}

const IntlContext = createContext<IntlContextType>({
  locale: defaultLocale,
  setLocale: () => undefined,
});

export const useIntlContext = () => useContext(IntlContext);

interface IntlContextProviderProps {
  children: ReactNode;
  locale: SupportedLocale;
  onLocaleChange?: (locale: SupportedLocale) => void;
}

export const IntlContextProvider: React.FC<IntlContextProviderProps> = ({
  children,
  locale,
  onLocaleChange,
}) => {
  const setLocale = useCallback(
    (next: SupportedLocale) => {
      setStoredLocale(next);
      onLocaleChange?.(next);
    },
    [onLocaleChange]
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
    }),
    [locale, setLocale]
  );

  return (
    <IntlContext.Provider value={value}>
      <IntlProvider
        locale={locale}
        messages={messages[locale]}
        defaultLocale={defaultLocale}
      >
        {children}
      </IntlProvider>
    </IntlContext.Provider>
  );
};
